import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import { Toaster } from "sonner";

// Lazy-loaded routes — smaller initial bundle, faster first paint.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Chat = lazy(() => import("./pages/Chat"));
const Voice = lazy(() => import("./pages/Voice"));
const Lessons = lazy(() => import("./pages/Lessons"));
const LessonDetail = lazy(() => import("./pages/LessonDetail"));
const Flashcards = lazy(() => import("./pages/Flashcards"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Profile = lazy(() => import("./pages/Profile"));
const Demo = lazy(() => import("./pages/Demo"));
const Review = lazy(() => import("./pages/Review"));
const Challenge = lazy(() => import("./pages/Challenge"));
const Dictionary = lazy(() => import("./pages/Dictionary"));
const EmbedPronounce = lazy(() => import("./pages/EmbedPronounce"));
const EmbedBuilder = lazy(() => import("./pages/EmbedBuilder"));
const NotFound = lazy(() => import("./pages/NotFound"));

const RouteFallback = () => (
  <div className="min-h-screen bg-navy-900 text-white flex items-center justify-center">
    <div className="h-10 w-10 rounded-full border-2 border-peach-500/40 border-t-peach-500 animate-spin" />
  </div>
);

const Protected = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/dictionary" element={<Dictionary />} />
                <Route path="/embed" element={<EmbedBuilder />} />
                <Route path="/embed/pronounce" element={<EmbedPronounce />} />
                <Route path="/app" element={<Protected><Dashboard /></Protected>} />
                <Route path="/app/chat" element={<Protected><Chat /></Protected>} />
                <Route path="/app/voice" element={<Protected><Voice /></Protected>} />
                <Route path="/app/lessons" element={<Protected><Lessons /></Protected>} />
                <Route path="/app/lessons/:id" element={<Protected><LessonDetail /></Protected>} />
                <Route path="/app/lessons/:id/quiz" element={<Protected><Quiz /></Protected>} />
                <Route path="/app/flashcards" element={<Protected><Flashcards /></Protected>} />
                <Route path="/app/flashcards/review" element={<Protected><Review /></Protected>} />
                <Route path="/app/challenge" element={<Protected><Challenge /></Protected>} />
                <Route path="/app/dictionary" element={<Protected><Dictionary /></Protected>} />
                <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
